import React, { useState } from "react";

/**
 * Stylized blocky USA silhouette with red pins.
 * Pure inline SVG — no Google Maps. Path simplified to evoke a 90s zine illustration.
 */
const USA_PATH =
  "M 60 110 L 80 90 L 110 78 L 150 70 L 200 64 L 260 60 L 320 58 L 380 56 L 440 58 L 500 64 L 560 74 L 610 88 L 660 108 L 720 130 L 760 156 L 790 186 L 800 220 L 790 250 L 770 274 L 740 290 L 710 296 L 680 290 L 660 280 L 620 282 L 600 296 L 580 306 L 560 318 L 540 326 L 510 336 L 490 348 L 470 360 L 450 358 L 430 350 L 410 340 L 388 326 L 370 308 L 350 296 L 330 290 L 308 286 L 286 282 L 264 274 L 244 262 L 226 250 L 210 234 L 196 218 L 184 200 L 174 180 L 162 162 L 148 150 L 130 142 L 110 138 L 90 132 L 72 124 Z";

// Stylized stripes overlay (positions in SVG coords)
const stripes = [
  { y: 96, w: 0 },
  { y: 130, w: 1 },
  { y: 170, w: 0 },
  { y: 220, w: 1 },
  { y: 270, w: 0 },
];

export const USMap = ({ pins = [], onPinClick, className = "" }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <div data-testid="us-map" className={`relative w-full ${className}`}>
      <svg
        viewBox="0 0 860 400"
        className="w-full h-auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="halftone"
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
          >
            <circle cx="2" cy="2" r="1.2" fill="rgba(255,255,255,0.18)" />
          </pattern>
        </defs>

        {/* Shadow offset block */}
        <path
          d={USA_PATH}
          transform="translate(8,8)"
          fill="#E63946"
          opacity="0.85"
        />

        {/* Main fill */}
        <path
          d={USA_PATH}
          fill="#F4F1EA"
          stroke="#0A1F44"
          strokeWidth="4"
          strokeLinejoin="miter"
        />

        {/* Halftone overlay */}
        <path d={USA_PATH} fill="url(#halftone)" opacity="0.9" />

        {/* Decorative horizontal stripes */}
        {stripes.map((s, i) => (
          <line
            key={i}
            x1="80"
            y1={s.y}
            x2="760"
            y2={s.y}
            stroke="#0A1F44"
            strokeWidth={s.w ? 1 : 0.6}
            strokeDasharray={s.w ? "0" : "4 6"}
            opacity="0.35"
          />
        ))}

        {/* Pins */}
        {pins.map((p, i) => {
          const isHover = hovered === i;
          return (
            <g
              key={i}
              transform={`translate(${p.x},${p.y})`}
              style={{ cursor: p.label ? "pointer" : "default" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onPinClick?.(p)}
              data-testid={`map-pin-${p.id || i}`}
            >
              {/* outer ring */}
              <circle
                r={isHover ? 18 : 14}
                fill="#E63946"
                stroke="#0A1F44"
                strokeWidth="3"
              />
              {/* inner dot */}
              <circle r={5} fill="#F4F1EA" />
              {p.label && (
                <g transform="translate(20,-22)">
                  <rect
                    x="-2"
                    y="-14"
                    width={p.label.length * 8 + 12}
                    height="22"
                    fill="#0A1F44"
                  />
                  <text
                    x="4"
                    y="2"
                    fill="#F4F1EA"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="11"
                    letterSpacing="1.5"
                  >
                    {p.label.toUpperCase()}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Compass / marker text */}
        <g transform="translate(720,360)">
          <text
            fill="#0A1F44"
            fontFamily="JetBrains Mono, monospace"
            fontSize="11"
            letterSpacing="2"
          >
            ◢ USA · 2026
          </text>
        </g>
      </svg>
    </div>
  );
};

export default USMap;
